import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { createQueryClient } from '@/api/queryClient';
import { App } from '@/App';
import { AppProviders } from '@/AppProviders';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { PlatformApp } from '@/platform/PlatformApp';
import { hideBootSplash } from '@/shell/bootSplash';
import '@/index.css';

/*
 * The platform panel is a second root on the same origin, chosen by path: `/platform` is the person
 * running the platform, everything else is a shop. Same origin on purpose — it keeps both refresh
 * cookies `SameSite=Lax` — and a separate root rather than a page in the shop's shell, because that
 * shell is built from a shop session the platform does not have.
 */
const isPlatform =
  window.location.pathname === '/platform' || window.location.pathname.startsWith('/platform/');

// Created once, outside the component tree — a client rebuilt on render loses every cache entry
// it exists to hold.
const queryClient = createQueryClient();

const container = document.getElementById('root');

if (!container) {
  throw new Error('index.html is missing its #root element');
}

createRoot(container).render(
  <StrictMode>
    {/*
      Inside the providers, not around them.

      The 500 screen is built from template classes and needs no Ant Design context, so either
      position renders. What decides it is what each one can catch: outside, a crash in
      `AppProviders` itself would be caught but every screen below would lose its theme on the way
      past; inside, the boundary sits exactly where the crashes actually happen — a page rendering
      against data it did not expect. Provider setup is a handful of statically-typed props and is
      not where this fails.
    */}
    <AppProviders queryClient={queryClient}>
      <ErrorBoundary>{isPlatform ? <PlatformApp /> : <App />}</ErrorBoundary>
    </AppProviders>
  </StrictMode>,
);

hideBootSplash();
